import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Users,
  Mountain,
  Hotel,
  Check,
  ArrowLeft,
  Sparkles,
  Utensils,
  BedDouble,
  Info,
  Snowflake,
  ChevronRight,
  FileText,
  GraduationCap,
  Clock,
  Shield,
  ClipboardList,
} from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import BookingCTAs from "@/components/BookingCTAs";
import TripGallery from "@/components/TripGallery";
import Testimonials from "@/components/Testimonials";
import { getTripBySlug, getPriceFrom, TRIPS } from "@/data/trips";

const SITE_URL = "https://clubamistad-madrid.com";

function fmtEuro(n) {
  return `${Number(n).toLocaleString("es-ES")} €`;
}

export default function TripDetailPage() {
  const { slug } = useParams();
  const trip = getTripBySlug(slug);
  if (!trip) return <Navigate to="/404" replace />;

  const others = TRIPS.filter((t) => t.slug !== trip.slug);
  const priceFrom = getPriceFrom(trip);
  const regimenCorto = (trip.regimen || "media pensión").split("—")[0].trim().toLowerCase();
  const comidasAparte = /completa/.test(regimenCorto)
    ? "las clases de esquí, el alquiler de material y el seguro"
    : "las clases de esquí, el alquiler de material, las comidas en pista y el seguro";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: trip.name,
    description: trip.intro,
    url: `${SITE_URL}/viajes-familia-esquiar/${trip.slug}`,
    image: trip.gallery?.length ? trip.gallery : trip.image,
    touristType: ["Familias con niños"],
    provider: {
      "@type": "TravelAgency",
      name: "Club Amistad",
      url: SITE_URL,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.7",
        reviewCount: "100",
        bestRating: "5",
        worstRating: "1",
      },
    },
    offers: {
      "@type": "Offer",
      price: priceFrom.amount,
      priceCurrency: "EUR",
      description: priceFrom.label
        ? `Precio desde, por persona, correspondiente a la categoría ${priceFrom.label} en habitación compartida con dos adultos. Incluye alojamiento, ${regimenCorto} y forfait; ${comidasAparte} se contratan aparte.`
        : `Precio desde, por persona, en habitación compartida. Incluye alojamiento, ${regimenCorto} y forfait.`,
      url: trip.booking_url || `${SITE_URL}/viajes-familia-esquiar/${trip.slug}`,
      availability: trip.sold_out
        ? "https://schema.org/SoldOut"
        : "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.7",
      reviewCount: "100",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Viajes en familia", item: `${SITE_URL}/#viajes` },
      { "@type": "ListItem", position: 3, name: trip.name, item: `${SITE_URL}/viajes-familia-esquiar/${trip.slug}` },
    ],
  };

  const faqLd = trip.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: trip.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const hotelLd = trip.hotel
    ? {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        name: trip.hotel.name,
        description: trip.hotel.subtitle,
        image: trip.gallery?.length ? trip.gallery[0] : trip.image,
        address: trip.hotel.address
          ? {
              "@type": "PostalAddress",
              streetAddress: trip.hotel.address,
              addressCountry: "ES",
            }
          : undefined,
        telephone: trip.hotel.phone,
        amenityFeature: trip.hotel.extra_info?.amenities?.map((a) => ({
          "@type": "LocationFeatureSpecification",
          name: a,
        })),
        starRating: /(\d+)★/.exec(trip.hotel.name)?.[1]
          ? { "@type": "Rating", ratingValue: /(\d+)★/.exec(trip.hotel.name)[1] }
          : undefined,
      }
    : null;

  const resortLd = trip.resort
    ? {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        name: `Estación de esquí ${trip.resort.name}`,
        description: trip.resort.subtitle || trip.resort.extra_info?.description,
        touristType: "Familias con niños",
        includesAttraction: trip.resort.stats
          ? [
              {
                "@type": "SkiResort",
                name: trip.resort.name,
                additionalProperty: [
                  trip.resort.stats.skiable_km && {
                    "@type": "PropertyValue",
                    name: "Kilómetros esquiables",
                    value: `${trip.resort.stats.skiable_km} km`,
                  },
                  trip.resort.stats.altitude && {
                    "@type": "PropertyValue",
                    name: "Altitud",
                    value: trip.resort.stats.altitude,
                  },
                  trip.resort.stats.snowmaking_km && {
                    "@type": "PropertyValue",
                    name: "Cañones de nieve",
                    value: `${trip.resort.stats.snowmaking_km} km innivados`,
                  },
                ].filter(Boolean),
              },
            ]
          : undefined,
      }
    : null;

  const jsonLdBundle = [jsonLd, breadcrumbLd, faqLd, hotelLd, resortLd].filter(Boolean);

  return (
    <div className="bg-brand-bg">
      <SEO
        title={trip.seo_title}
        description={trip.seo_description}
        path={`/viajes-familia-esquiar/${trip.slug}`}
        image={trip.image}
        jsonLd={jsonLdBundle}
      />

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <img
          src={trip.hero_image || trip.image}
          alt={`Familia esquiando en ${trip.short_name} — ${trip.season}`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/80" />
        <div className="container-prose relative z-10 py-20 md:py-28 text-white">
          <motion.nav
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-white/70 mb-6"
            aria-label="Miga de pan"
          >
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/#viajes" className="hover:text-white">Viajes</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-white">{trip.short_name}</span>
          </motion.nav>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-cormorant italic text-sm tracking-[0.24em] uppercase text-brand-warm"
          >
            {trip.season} · {trip.duration}
          </motion.p>
          {trip.sold_out && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              data-testid="trip-soldout-badge"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 font-outfit text-sm font-semibold uppercase tracking-[0.16em] text-brand-primary shadow-lg"
            >
              Completo · sin plazas disponibles
            </motion.div>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 max-w-4xl text-white leading-[1.05]"
          >
            {trip.name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-5 max-w-2xl text-base md:text-xl text-white/90"
          >
            {trip.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-white/95 text-sm md:text-base"
          >
            <span className="inline-flex items-center gap-2"><Calendar className="h-5 w-5 text-brand-warm" /> {trip.dates}</span>
            <span className="inline-flex items-center gap-2"><Users className="h-5 w-5 text-brand-warm" /> {trip.family_basis}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10"
          >
            <BookingCTAs trip={trip} variant="on-dark" />
          </motion.div>
        </div>
      </section>

      {/* FACTS BAR + PAIN HOOK */}
      <section className="border-b border-brand-line">
        <div className="container-prose py-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {trip.regimen || trip.room_distribution || trip.ski_days || trip.hotel?.name ? (
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-3">
              {trip.hotel?.name && (
                <FactCard icon={Hotel} label="Alojamiento" value={trip.hotel.name} />
              )}
              {trip.regimen && <FactCard icon={Utensils} label="Régimen" value={trip.regimen} />}
              {trip.room_distribution && (
                <FactCard icon={BedDouble} label="Habitaciones" value={trip.room_distribution} />
              )}
              {trip.ski_days && (
                <FactCard icon={Snowflake} label="Días de esquí" value={`${trip.ski_days} días`} />
              )}
            </div>
          ) : null}

          <div className={`${trip.extended ? "lg:col-span-5" : "lg:col-span-12"}`}>
            <p className="overline">Lo entendemos</p>
            <p className="mt-3 font-cormorant italic text-2xl md:text-3xl text-brand-primary leading-snug">
              {trip.pain_hook}
            </p>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-brand-muted">Desde</p>
              <p className="font-outfit text-4xl md:text-5xl text-brand-primary font-semibold tracking-tight">
                {fmtEuro(priceFrom.amount)}
                <span className="text-base md:text-lg font-normal text-brand-muted">
                  {priceFrom.label ? ` / ${priceFrom.label.toLowerCase()}` : " / persona"}
                </span>
              </p>
              {priceFrom.adult && (
                <p className="text-sm text-brand-muted mt-2" data-testid="price-from-note">
                  Precio por persona en habitación familiar. Adulto desde {fmtEuro(priceFrom.adult)}.
                  Ver tabla completa de tarifas por edades más abajo.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {trip.gallery && trip.gallery.length > 0 && (
        <section className="section-y">
          <div className="container-prose">
            <div className="max-w-2xl mb-10">
              <p className="overline">Un vistazo al destino</p>
              <h2 className="h2-display mt-3">Así se ven vuestras vacaciones a la nieve en {trip.short_name}.</h2>
            </div>
            <TripGallery images={trip.gallery} alt={`Viaje a la nieve en ${trip.short_name} — ${trip.season}, hotel a pie de pista y familia esquiando`} />
          </div>
        </section>
      )}

      {/* HOTEL + STATION */}
      <section className="section-y bg-brand-surface/60">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="card-soft p-8 md:p-10">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary mb-5">
              <Hotel className="h-5 w-5" />
            </div>
            <p className="overline">Hotel a pie de pista</p>
            <h2 className="font-outfit text-3xl text-brand-primary font-semibold mt-2">{trip.hotel.name}</h2>
            {trip.hotel.subtitle && (
              <p className="mt-2 text-brand-muted font-manrope">{trip.hotel.subtitle}</p>
            )}
            <ul className="mt-6 space-y-4">
              {trip.hotel.highlights.map((h) => (
                <li key={h} className="flex gap-3">
                  <Check className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span className="font-manrope text-brand-ink/85">{h}</span>
                </li>
              ))}
            </ul>

            {trip.hotel.extra_info && (
              <details className="group mt-6 border-t border-brand-line pt-6" data-testid="hotel-more-info">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-outfit text-brand-primary font-medium">Más info sobre el hotel</span>
                  <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                    <span className="text-lg leading-none">+</span>
                  </span>
                </summary>
                <div className="mt-5 space-y-5 text-sm md:text-base font-manrope text-brand-ink/85">
                  {trip.hotel.extra_info.description && (
                    <p className="leading-relaxed">{trip.hotel.extra_info.description}</p>
                  )}
                  {trip.hotel.extra_info.services?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-brand-muted mb-2">Servicios</p>
                      <div className="flex flex-wrap gap-2">
                        {trip.hotel.extra_info.services.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-center rounded-full bg-brand-accent/10 text-brand-accent px-3 py-1 text-xs font-medium"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {trip.hotel.extra_info.amenities?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-brand-muted mb-2">Instalaciones</p>
                      <div className="flex flex-wrap gap-2">
                        {trip.hotel.extra_info.amenities.map((a) => (
                          <span
                            key={a}
                            className="inline-flex items-center rounded-full bg-brand-primary/8 text-brand-primary px-3 py-1 text-xs font-medium"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(trip.hotel.address || trip.hotel.phone) && (
                    <div className="pt-2 text-xs md:text-sm text-brand-muted space-y-1">
                      {trip.hotel.address && (
                        <p><span className="font-medium text-brand-ink/70">Dirección:</span> {trip.hotel.address}</p>
                      )}
                      {trip.hotel.phone && (
                        <p><span className="font-medium text-brand-ink/70">Teléfono:</span> {trip.hotel.phone}</p>
                      )}
                    </div>
                  )}
                  {trip.hotel.extra_info.map_query && (
                    <div className="mt-4 rounded-2xl overflow-hidden border border-brand-line">
                      <iframe
                        title={`Mapa · ${trip.hotel.name}`}
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(trip.hotel.extra_info.map_query)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                        loading="lazy"
                        className="w-full h-64 md:h-72 border-0"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>

          <div className="card-soft p-8 md:p-10">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary mb-5">
              <Mountain className="h-5 w-5" />
            </div>
            <p className="overline">Estación de esquí</p>
            <h2 className="font-outfit text-3xl text-brand-primary font-semibold mt-2">Estación de esquí {trip.resort.name}</h2>
            {trip.resort.subtitle && (
              <p className="mt-2 text-brand-muted font-manrope">{trip.resort.subtitle}</p>
            )}
            <ul className="mt-6 space-y-4">
              {trip.resort.highlights.map((h) => (
                <li key={h} className="flex gap-3">
                  <Check className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span className="font-manrope text-brand-ink/85">{h}</span>
                </li>
              ))}
            </ul>

            {(trip.resort.stats || trip.resort.extra_info) && (
              <details className="group mt-6 border-t border-brand-line pt-6" data-testid="resort-more-info">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-outfit text-brand-primary font-medium">Más info sobre la estación</span>
                  <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                    <span className="text-lg leading-none">+</span>
                  </span>
                </summary>
                <div className="mt-5 space-y-5 text-sm md:text-base font-manrope text-brand-ink/85">
                  {trip.resort.extra_info?.description && (
                    <p>{trip.resort.extra_info.description}</p>
                  )}
                  {trip.resort.stats && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-brand-muted mb-3">La estación en datos</p>
                      <div className="grid grid-cols-2 gap-3">
                        <StatChip label="Km esquiables" value={`${trip.resort.stats.skiable_km} km`} />
                        <StatChip label="Pistas totales" value={`${trip.resort.stats.slopes_total}`} />
                        <StatChip label="Km innivados" value={`${trip.resort.stats.snowmaking_km} km`} />
                        <StatChip label="Cota" value={trip.resort.stats.altitude} />
                      </div>
                      {trip.resort.stats.slopes_breakdown && (
                        <div className="mt-4 flex flex-wrap gap-2 text-xs">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 text-green-700 px-3 py-1 border border-green-200">
                            <span className="h-2 w-2 rounded-full bg-green-500" /> {trip.resort.stats.slopes_breakdown.green} verdes
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3 py-1 border border-blue-200">
                            <span className="h-2 w-2 rounded-full bg-blue-500" /> {trip.resort.stats.slopes_breakdown.blue} azules
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 px-3 py-1 border border-red-200">
                            <span className="h-2 w-2 rounded-full bg-red-500" /> {trip.resort.stats.slopes_breakdown.red} rojas
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 text-stone-800 px-3 py-1 border border-stone-300">
                            <span className="h-2 w-2 rounded-full bg-stone-800" /> {trip.resort.stats.slopes_breakdown.black} negras
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {trip.resort.extra_info?.connections && (
                    <p><span className="font-medium text-brand-ink/70">Conectividad: </span>{trip.resort.extra_info.connections}</p>
                  )}
                  {trip.resort.extra_info?.note && (
                    <p className="text-brand-muted text-xs">{trip.resort.extra_info.note}</p>
                  )}
                  {trip.resort.extra_info?.map_query && (
                    <div className="mt-4 rounded-2xl overflow-hidden border border-brand-line">
                      <iframe
                        title={`Mapa · ${trip.resort.name}`}
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(trip.resort.extra_info.map_query)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                        loading="lazy"
                        className="w-full h-64 md:h-72 border-0"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </section>

      {/* PRICES + INCLUDES */}
      <section className="section-y">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-10">
          {trip.prices && trip.prices.length > 0 && (
            <div className="lg:col-span-7">
              <p className="overline">
                Precios · Alojamiento, {regimenCorto} y forfait incluidos
              </p>
              <h2 className="h2-display mt-3">Precio del viaje a {trip.short_name}: sencillo y sin sorpresas.</h2>
              <p className="body-lg mt-4 max-w-xl">
                {trip.sold_out
                  ? `Precios de referencia de esta salida, que está completa. Incluyen alojamiento, ${regimenCorto} y forfait; ${comidasAparte} se contratan aparte.`
                  : `Los precios son por persona en habitación compartida con la familia e incluyen alojamiento, ${regimenCorto} y forfait. Por su parte, ${comidasAparte} se contratan aparte (ver suplementos).`}
              </p>

              <div className="mt-8 card-soft overflow-hidden" data-testid="price-table">
                <table className="w-full text-sm md:text-base">
                  <tbody>
                    {trip.prices.map((p, i) => (
                      <tr
                        key={p.label}
                        className={`${i > 0 ? "border-t border-brand-line" : ""} ${
                          p.free ? "bg-brand-accent/5" : ""
                        }`}
                      >
                        <td className="px-5 md:px-7 py-4 md:py-5">
                          <p className="font-outfit font-medium text-brand-primary">{p.label}</p>
                          {p.note && (
                            <p className="text-xs md:text-sm text-brand-muted mt-0.5">{p.note}</p>
                          )}
                        </td>
                        <td className="px-5 md:px-7 py-4 md:py-5 text-right whitespace-nowrap">
                          {p.free ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent/15 text-brand-accent px-3 py-1 text-xs md:text-sm font-medium uppercase tracking-wider">
                              Gratuito
                            </span>
                          ) : (
                            <span className="font-outfit font-semibold text-brand-primary text-xl md:text-2xl">
                              {fmtEuro(p.amount)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {trip.notes && trip.notes.length > 0 && (
                <ul className="mt-5 space-y-1.5 text-xs md:text-sm text-brand-muted">
                  {trip.notes.map((n) => (
                    <li key={n} className="flex gap-2">
                      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className={`${trip.prices?.length ? "lg:col-span-5" : "lg:col-span-12"}`}>
            <p className="overline">Nuestro Especial Familias</p>
            <h2 className="h2-display mt-3">
              {trip.includes && trip.includes.length ? "Qué incluye el viaje" : "Todo pensado para vosotros"}
            </h2>
            <p className="body-lg mt-4">
              Acompañan monitores propios del Club Amistad. Clases opcionales para niños todo el día
              (desde 4 años) y para adultos 2 h/día. Juegos y actividades supervisadas por las tardes.
            </p>

            <ul className="mt-8 space-y-4">
              {(trip.includes && trip.includes.length ? trip.includes : DEFAULT_INCLUDED).map((item) => (
                <li key={item} className="flex gap-3">
                  <div className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary mt-0.5">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-manrope text-brand-ink/85">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SUPPLEMENTS */}
      {trip.supplements && trip.supplements.length > 0 && (
        <section className="section-y bg-brand-surface/60">
          <div className="container-prose">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
              <div className="lg:col-span-7">
                <p className="overline">Extras opcionales</p>
                <h2 className="h2-display mt-3">Suplementos: clases de esquí, alquiler y comidas.</h2>
              </div>
              <p className="lg:col-span-5 body-lg">
                Añade solo aquello que mejore vuestra experiencia. Todo es opcional y a precio cerrado
                — sin sorpresas al llegar.
              </p>
            </div>

            <div className="card-soft overflow-hidden" data-testid="supplements-table">
              <ul>
                {trip.supplements.map((s, i) => (
                  <li
                    key={s.label}
                    className={`px-5 md:px-7 py-4 md:py-5 flex items-center justify-between gap-6 ${
                      i > 0 ? "border-t border-brand-line" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-4 w-4 text-brand-accent mt-1 shrink-0" />
                      <div>
                        <p className="font-manrope text-brand-ink">{s.label}</p>
                        {s.note && <p className="text-xs text-brand-muted mt-0.5">{s.note}</p>}
                      </div>
                    </div>
                    <span className="font-outfit font-semibold text-brand-primary text-lg md:text-xl whitespace-nowrap">
                      {fmtEuro(s.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* PROGRAM: CLASES / TARDES / SEGUROS / ANTES DE VIAJAR */}
      {trip.program && (
        <section className="section-y">
          <div className="container-prose">
            <div className="max-w-3xl mb-14">
              <p className="overline">Cómo se organizan los días</p>
              <h2 className="h2-display mt-3">Programa del viaje: clases, tardes, seguros y qué llevar.</h2>
              <p className="body-lg mt-5">
                Estos son los detalles prácticos que hacen que la semana funcione: cómo son las clases,
                qué pasa por las tardes, qué cubren los seguros y qué esperar antes de coger la maleta.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Clases */}
              {trip.program.classes && (
                <div className="lg:col-span-7 card-soft p-8 md:p-10" data-testid="program-classes">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                      <GraduationCap className="h-5 w-5" />
                    </div>
                    <p className="overline">Clases de esquí</p>
                  </div>
                  <h3 className="font-outfit text-2xl md:text-3xl text-brand-primary font-semibold">
                    Monitores propios, grupos pequeños, comida con los profes.
                  </h3>
                  <p className="mt-4 font-manrope text-brand-ink/85 leading-relaxed">
                    {trip.program.classes.intro}
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-brand-line bg-brand-surface/60 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-brand-muted">Edad mínima</p>
                      <p className="mt-1 font-outfit font-semibold text-brand-primary">
                        {trip.program.classes.min_age}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-brand-line bg-brand-surface/60 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-brand-muted">Horario</p>
                      <p className="mt-1 font-outfit font-semibold text-brand-primary">
                        {trip.program.classes.schedule}
                      </p>
                    </div>
                  </div>

                  {trip.program.classes.groups?.length > 0 && (
                    <div className="mt-6 divide-y divide-brand-line rounded-2xl border border-brand-line overflow-hidden">
                      {trip.program.classes.groups.map((g) => (
                        <div key={g.ages} className="p-4 md:p-5 bg-white">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                              <p className="font-outfit font-medium text-brand-primary">{g.ages}</p>
                              {g.label && (
                                <p className="text-xs uppercase tracking-wider text-brand-accent mt-0.5">
                                  {g.label}
                                </p>
                              )}
                            </div>
                            <p className="text-sm text-brand-muted">{g.group_size}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {trip.program.classes.meal_note && (
                    <div className="mt-6 flex gap-3 rounded-2xl bg-brand-accent/5 border border-brand-accent/20 p-4">
                      <Utensils className="h-4 w-4 text-brand-accent shrink-0 mt-0.5" />
                      <p className="text-sm text-brand-ink/85 font-manrope">
                        {trip.program.classes.meal_note}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="lg:col-span-5 space-y-6">
                {trip.program.evening_activities && (
                  <div className="card-soft p-8" data-testid="program-evenings">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                        <Clock className="h-5 w-5" />
                      </div>
                      <p className="overline">Actividades por la tarde</p>
                    </div>
                    <h3 className="font-outfit text-xl text-brand-primary font-semibold">
                      {trip.program.evening_activities.schedule}
                    </h3>
                    <p className="mt-3 font-manrope text-sm text-brand-ink/80 leading-relaxed">
                      {trip.program.evening_activities.description}
                    </p>
                  </div>
                )}

                {trip.program.insurance && (
                  <div className="card-soft p-8" data-testid="program-insurance">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                        <Shield className="h-5 w-5" />
                      </div>
                      <p className="overline">Seguros — con transparencia</p>
                    </div>
                    <p className="font-manrope text-sm text-brand-ink/80 leading-relaxed">
                      {trip.program.insurance.included}
                    </p>
                    {trip.program.insurance.recommended?.length > 0 && (
                      <>
                        <p className="mt-4 text-xs uppercase tracking-wider text-brand-muted">
                          Recomendados
                        </p>
                        <ul className="mt-2 space-y-2">
                          {trip.program.insurance.recommended.map((r) => (
                            <li key={r} className="flex gap-2 text-sm text-brand-ink/85">
                              <Check className="h-4 w-4 text-brand-accent shrink-0 mt-0.5" />
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </div>

              {trip.program.before_travel?.length > 0 && (
                <div className="lg:col-span-12 card-soft p-8 md:p-10" data-testid="program-before-travel">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                      <ClipboardList className="h-5 w-5" />
                    </div>
                    <p className="overline">Antes de viajar</p>
                  </div>
                  <h3 className="font-outfit text-2xl text-brand-primary font-semibold">
                    Os acompañamos desde el minuto uno.
                  </h3>
                  <ul className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {trip.program.before_travel.map((b, i) => (
                      <li
                        key={b}
                        className="rounded-2xl border border-brand-line bg-brand-surface/60 p-5"
                      >
                        <p className="font-cormorant italic text-brand-accent text-xs uppercase tracking-[0.2em]">
                          Paso 0{i + 1}
                        </p>
                        <p className="mt-2 font-manrope text-sm text-brand-ink/85 leading-relaxed">
                          {b}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PDF BANNER — highlighted download */}
      {trip.pdf_url && (
        <section className="py-14 md:py-16">
          <div className="container-prose">
            <div
              className="relative overflow-hidden rounded-[2rem] bg-brand-primary text-white p-8 md:p-14"
              data-testid="pdf-banner"
            >
              {/* decorative rings */}
              <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-accent/20 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-brand-warm/10 blur-3xl" />

              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-2 flex lg:justify-center">
                  <div className="inline-flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-3xl bg-white/10 border border-white/20 backdrop-blur-sm">
                    <FileText className="h-10 w-10 md:h-12 md:w-12 text-brand-warm" strokeWidth={1.5} />
                  </div>
                </div>

                <div className="lg:col-span-6">
                  <p className="font-cormorant italic text-sm uppercase tracking-[0.24em] text-brand-warm">
                    Todo por escrito · para leer con calma
                  </p>
                  <h2 className="mt-3 font-outfit text-3xl md:text-4xl font-semibold leading-tight">
                    Llévate el dossier completo del viaje.
                  </h2>
                  <p className="mt-4 font-manrope text-white/85 leading-relaxed max-w-xl">
                    Todos los detalles del hotel, la estación, precios, clases, seguros y condiciones — en
                    un solo PDF pensado para que lo revises con tu pareja, sin prisa y sin pop-ups.
                  </p>
                </div>

                <div className="lg:col-span-4 lg:text-right">
                  <a
                    href={trip.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid={`pdf-banner-download-${trip.slug}`}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-accent hover:bg-brand-accentHover text-white px-7 py-4 font-manrope font-medium text-base transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <FileText className="h-5 w-5" />
                    Descargar dossier (PDF)
                  </a>
                  <p className="mt-3 text-xs text-white/60">Peso ~1 MB · Se abre en una pestaña nueva</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* DUAL CTA */}
      <section className="section-y">
        <div className="container-prose">
          <div className="card-soft p-8 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                {trip.sold_out ? (
                  <>
                    <p className="overline">Este viaje está completo</p>
                    <h2 className="h2-display mt-3 max-w-2xl">
                      Las plazas de {trip.short_name} en {trip.season.toLowerCase()} se han agotado.
                    </h2>
                    <p className="body-lg mt-5 max-w-2xl">
                      Ya no es posible reservar esta salida. Si te interesaban estas fechas, cuéntanoslo:
                      te avisamos si se libera alguna plaza y te proponemos otros viajes con el mismo
                      formato familiar y fechas parecidas.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="overline">Dos formas de reservar</p>
                    <h2 className="h2-display mt-3 max-w-2xl">
                      Que un asesor os llame y lo veamos juntos, o reserva online en 2 minutos.
                    </h2>
                    <p className="body-lg mt-5 max-w-2xl">
                      Si es la primera vez que os planteáis un viaje a la nieve o queréis matizar algún
                      detalle, un asesor especializado en familias os llamará para resolver dudas sin
                      compromiso. Si ya lo tienes claro, en un par de clics reservas online.
                    </p>
                  </>
                )}
              </div>
              <div className="lg:col-span-5">
                <BookingCTAs trip={trip} className="lg:items-end" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {trip.faqs && trip.faqs.length > 0 && (
        <section className="section-y bg-brand-surface/60">
          <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline">Preguntas frecuentes sobre este viaje de esquí</p>
              <h2 className="h2-display mt-3">Preguntas frecuentes sobre el viaje a {trip.short_name}.</h2>
              <p className="body-lg mt-5">
                Estas son las dudas que más nos hacéis. Si te queda alguna otra, escríbenos — no hay
                pregunta pequeña cuando es la primera vez.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="divide-y divide-brand-line rounded-2xl border border-brand-line bg-white" data-testid="faq-list">
                {trip.faqs.map((f, i) => (
                  <details key={f.q} className="group px-6 md:px-8 py-5 md:py-6" data-testid={`faq-${i}`}>
                    <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                      <span className="font-outfit text-lg text-brand-primary font-medium">{f.q}</span>
                      <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                        <span className="text-lg leading-none">+</span>
                      </span>
                    </summary>
                    <p className="mt-4 font-manrope text-brand-ink/80 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BOOKING CONDITIONS + DISCOUNTS */}
      {trip.booking_info && (
        <section className="section-y">
          <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline">Cómo funciona la reserva</p>
              <h2 className="h2-display mt-3">Reserva, pago y política de cancelación.</h2>
              <p className="body-lg mt-5">
                Aquí lo tienes todo: cómo se paga, qué descuentos hay para vuestra familia y qué pasa si
                surge un imprevisto. Sin frases raras ni asteriscos escondidos.
              </p>
              {trip.pdf_url && (
                <a
                  href={trip.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`pdf-conditions-${trip.slug}`}
                  className="btn-ghost mt-6"
                >
                  <FileText className="h-4 w-4" /> Descargar dossier completo (PDF)
                </a>
              )}
            </div>

            <div className="lg:col-span-8 space-y-4" data-testid="booking-conditions">
              {(trip.booking_info.deposit || trip.booking_info.payment || trip.booking_info.confirmation) && (
                <details className="group card-soft p-6 md:p-7" open data-testid="conditions-payment">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="font-outfit text-lg text-brand-primary font-medium">Cómo se paga</span>
                    <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <div className="mt-4 space-y-3 text-brand-ink/85 font-manrope">
                    {trip.booking_info.deposit && <p>{trip.booking_info.deposit}</p>}
                    {trip.booking_info.payment && <p>{trip.booking_info.payment}</p>}
                    {trip.booking_info.confirmation && (
                      <p className="text-sm text-brand-muted">{trip.booking_info.confirmation}</p>
                    )}
                  </div>
                </details>
              )}

              {trip.booking_info.discounts?.length > 0 && (
                <details className="group card-soft p-6 md:p-7" data-testid="conditions-discounts">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="font-outfit text-lg text-brand-primary font-medium">Descuentos disponibles</span>
                    <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <ul className="mt-4 space-y-3">
                    {trip.booking_info.discounts.map((d) => (
                      <li key={d} className="flex gap-3">
                        <Sparkles className="h-4 w-4 text-brand-accent shrink-0 mt-1" />
                        <span className="font-manrope text-brand-ink/85">{d}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {trip.booking_info.cancellation?.length > 0 && (
                <details className="group card-soft p-6 md:p-7" data-testid="conditions-cancellation">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="font-outfit text-lg text-brand-primary font-medium">Política de cancelación</span>
                    <span className="shrink-0 h-8 w-8 rounded-full border border-brand-line inline-flex items-center justify-center text-brand-primary transition-transform group-open:rotate-45">
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <div className="mt-4 overflow-hidden rounded-xl border border-brand-line">
                    <table className="w-full text-sm">
                      <tbody>
                        {trip.booking_info.cancellation.map((c, i) => (
                          <tr key={c.when} className={i > 0 ? "border-t border-brand-line" : ""}>
                            <td className="px-4 py-3 font-manrope text-brand-ink/80">{c.when}</td>
                            <td className="px-4 py-3 text-right font-outfit font-medium text-brand-primary whitespace-nowrap">
                              {c.amount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {trip.booking_info.guarantee && (
                    <p className="mt-4 text-sm text-brand-ink/75 bg-brand-accent/5 border border-brand-accent/20 rounded-xl p-4">
                      <Sparkles className="h-4 w-4 text-brand-accent inline mr-1.5 -mt-0.5" />
                      {trip.booking_info.guarantee}
                    </p>
                  )}
                </details>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT FORM */}
      <section id="reservar" className="section-y">
        <div className="container-prose max-w-4xl">
          <ContactForm trip={trip} />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials
        eyebrow="Confían en Club Amistad"
        title="Otras familias ya vivieron su viaje de esquí con nosotros."
        intro="Recopilamos aquí opiniones reales publicadas por clientes en nuestra ficha de Google Business. Ninguna está retocada: puedes contrastarlas directamente en Google."
      />

      {/* OTHER TRIPS */}
      <section className="section-y bg-brand-surface/40">
        <div className="container-prose">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <h2 className="h2-display">Otros viajes que podrían encajar con vosotros</h2>
            <Link to="/" className="btn-ghost">
              <ArrowLeft className="h-4 w-4" /> Ver todos
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/viajes-familia-esquiar/${o.slug}`}
                data-testid={`other-trip-${o.slug}`}
                className="card-soft overflow-hidden group block"
              >
                <div className="aspect-[4/3] relative overflow-hidden bg-brand-surface">
                  <img
                    src={o.hero_image || o.image}
                    alt={o.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-wider text-brand-accent">{o.season}</p>
                  <h3 className="font-outfit text-xl font-semibold text-brand-primary mt-1">{o.name}</h3>
                  <p className="text-sm text-brand-muted mt-1">{o.dates_short}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="card-soft p-4 md:p-5">
      <div className="flex items-center gap-2 text-brand-accent">
        <Icon className="h-4 w-4" />
        <p className="text-[10px] md:text-xs uppercase tracking-wider font-medium">{label}</p>
      </div>
      <p className="mt-2 font-outfit font-medium text-brand-primary leading-tight text-sm md:text-base">
        {value}
      </p>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="rounded-xl border border-brand-line bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider text-brand-muted">{label}</p>
      <p className="mt-1 font-outfit font-semibold text-brand-primary">{value}</p>
    </div>
  );
}

const DEFAULT_INCLUDED = [
  "Alojamiento a pie de pista",
  "Media pensión con desayuno y cena buffet",
  "Forfait para toda la familia",
  "Actividades de tarde para niños",
  "Guía y personal Club Amistad en destino",
  "Atención personalizada antes, durante y después",
];
